import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowSquareOut, CalendarBlank, ChartBar, ChatText, Check, CheckCircle, Coins, FileText, Funnel, Hourglass, ListBullets, PaperPlaneTilt, Receipt, SealWarning,
  Sparkle, Star, Timer, UploadSimple, Wrench, type Icon,
} from '@phosphor-icons/react';
import {
  FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, PanelZeile, SchalterZeile, SearchSelect, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { objektName } from '../../../../../lib/immobilien/objektKopf';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import {
  auftraegeJeMonat, freigabeNoetig, KRITERIEN, kriterienSchnitt, naechsteStufe, passtTicketFilter, RECHNUNG_STUFE, reaktionsStunden, rechnungFehler, rechnungKennzahlen,
  rechnungPayload, rechnungStufe, steuerAufteilung, stufenZaehler, type RechnungEntwurf, type TicketFilter,
} from '../../../../../lib/dienstleister/auftraege';
import { bauabzugNoetig, komma1, sterne, stufeLabel, TICKET_STUFEN, ticketStufe, type TicketStufe } from '../../../../../lib/dienstleister/detail';
import { zuordnungLaeuft } from '../../../../../lib/dienstleister/liste';
import { zahl } from '../../../../../lib/dienstleister/neuerDienstleister';
import { prioTon } from '../../../../../lib/dienstleister/vorschlag';
import {
  bezahleRechnung, erfasseRechnung, erteileAuftrag, frageFreigabeAn, freigabeEmpfaenger, pruefeRechnung, setzeTicketStufe, speichereBewertung,
} from '../../../../../lib/dienstleister/aktionen';
import { meldeDlGeaendert, type DlTicket } from '../../../../../lib/dienstleister/useDienstleisterDetail';
import { GOLD, GOLD_SOFT } from '../../liste/DienstleisterKachel';
import { useDlSeite } from '../DienstleisterSeitenKontext';
import { BEREICH_STIL, fehlerText, GLAS } from './teile';

const STUFE_ICON: Record<TicketStufe, Icon> = { neu: Sparkle, beauftragt: PaperPlaneTilt, in_arbeit: Wrench, erledigt: CheckCircle, abgerechnet: Hourglass };
const mieterName = (t: DlTicket) => (t.mieter ? (t.mieter.firma_name || [t.mieter.vorname, t.mieter.nachname].filter(Boolean).join(' ')).trim() : '');
const stunden = (h: number | null) => (h === null ? '–' : `${komma1(h)} h`);

// ── Aufträge & Tickets (Screens 14, 15, 40) ────────────────────────────────────
export function AuftraegePanel() {
  const s = useDlSeite();
  const navigate = useNavigate();
  const openKontakt = useOpenKontakt();
  const d = s.daten.dienstleister;
  const z = s.daten.zeile;
  const [toast, zeige] = useToastPill();
  const [filter, setFilter] = useState<TicketFilter>('offen');
  const [laeuft, setLaeuft] = useState(false);
  const objName = useMemo(() => new Map(s.daten.objekte.map(o => [o.id, objektName(o)])), [s.daten.objekte]);
  const zaehler = stufenZaehler(s.daten.tickets);
  const reaktion = reaktionsStunden(s.daten.tickets, s.daten.historie);
  const liste = s.daten.tickets.filter(t => passtTicketFilter(t.status, filter));

  // Auftrag erteilen
  const zugeordnet = s.daten.zuordnungen.filter(o => zuordnungLaeuft(o, s.heute)).map(o => o.objekt_id);
  const [objektId, setObjektId] = useState<string | null>(zugeordnet[0] ?? null);
  const [text, setText] = useState('');
  const [wunsch, setWunsch] = useState('');
  const [kosten, setKosten] = useState('');
  const grenze = Number(d.freigabegrenze ?? 500);
  const kostenZahl = zahl(kosten);
  const kostenFehler = kostenZahl !== null && (Number.isNaN(kostenZahl) || kostenZahl < 0) ? 'Zahl ≥ 0' : undefined;
  const ueberGrenze = !kostenFehler && freigabeNoetig(kostenZahl, grenze);
  const [freigabe, setFreigabe] = useState(true);
  const objektOptionen = useMemo(() => s.daten.objekte.map(o => ({ id: o.id, label: objektName(o), sub: [o.objektnummer, (o.stadt ?? o.ort ?? '').trim()].filter(Boolean).join(' · '), suchtext: [o.objektnummer, o.strasse, o.stadt].filter(Boolean).join(' ') })), [s.daten.objekte]);

  const weiter = async (t: DlTicket) => {
    const n = naechsteStufe(ticketStufe(t.status));
    if (!n) return;
    setLaeuft(true);
    try {
      await setzeTicketStufe({ id: t.id, ticket_nummer: t.ticket_nummer, status: t.status, zustaendig_id: t.zustaendig_id, beauftragt_am: t.beauftragt_am }, n);
      zeige(`${t.ticket_nummer ?? 'Ticket'}: ${stufeLabel(n)}`);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht geändert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  const sende = async () => {
    if (!text.trim() || kostenFehler) return;
    setLaeuft(true);
    try {
      const t = await erteileAuftrag(d.id, { objektId, text, wunschtermin: wunsch, kostenrahmen: kostenZahl !== null && !Number.isNaN(kostenZahl) ? kostenZahl : null, angebot: z.angebot });
      let freigabeText = '';
      if (ueberGrenze && freigabe && objektId && kostenZahl !== null) {
        const empf = await freigabeEmpfaenger(objektId, s.heute);
        if (empf && 'eigentuemerId' in empf) {
          await frageFreigabeAn(empf.eigentuemerId, { objektId, beschreibung: `${t.ticket_nummer}: ${text.trim().slice(0, 120)}`, betrag: kostenZahl, dienstleister: s.name, dienstleisterId: d.id });
          freigabeText = ` · Freigabe bei ${empf.name} angefragt`;
        } else if (empf && 'weg' in empf) freigabeText = ' · WEG-Beschluss nötig';
        else freigabeText = ' · Freigabe beim Eigentümer selbst erfassen';
      }
      zeige(`Ticket ${t.ticket_nummer} angelegt${freigabeText}`);
      meldeDlGeaendert(d.id);
      navigate(`/admin/tickets/${encodeURIComponent(t.id)}?beauftragen=${z.angebot ? 'angebot' : 'auftrag'}`);
    } catch (err) { zeige(`Nicht angelegt: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Neu" wert={zaehler.neu} icon={Sparkle} ton="info" />
        <KpiTile layout="kachel" label="Beauftragt" wert={zaehler.beauftragt} icon={PaperPlaneTilt} ton="acc" />
        <KpiTile layout="kachel" label="In Arbeit" wert={zaehler.in_arbeit} icon={Wrench} ton="warn" />
        <KpiTile layout="kachel" label="Ø Reaktionszeit" wert={stunden(reaktion)} sub={reaktion === null ? 'noch keine Messung' : undefined} icon={Timer} ton="ok" />
      </KpiReihe>
      <InnenKarte style={GLAS}>
        <KartenTitel>Filter</KartenTitel>
        <SegmentedControl<TicketFilter> ariaLabel="Filter" hoehe={34} className="self-start" wert={filter} onChange={setFilter}
          segmente={[{ key: 'offen', label: 'Offen', icon: Hourglass }, { key: 'erledigt', label: 'Erledigt', icon: Check }, { key: 'alle', label: 'Alle', icon: ListBullets }]} />
      </InnenKarte>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts={<ImmoButton groesse={36} icon={ArrowSquareOut} onClick={() => navigate('/admin/tickets')}>Zum Ticket-Bereich</ImmoButton>}>
            {`${liste.length} ${liste.length === 1 ? 'Ticket' : 'Tickets'}`}
          </KartenTitel>
        </div>
        {liste.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Tickets in diesem Filter</span>}
        {liste.map(t => {
          const stufe = ticketStufe(t.status);
          const n = naechsteStufe(stufe);
          const info = TICKET_STUFEN.find(x => x.key === stufe)!;
          const mieter = mieterName(t);
          const sub = [t.objekt_id ? objName.get(t.objekt_id) ?? '' : '', mieter, deDatum(t.created_at.slice(0, 10)), t.prioritaet ? `Priorität ${t.prioritaet}` : '', n ? `antippen: → ${stufeLabel(n)}` : ''].filter(Boolean).join(' · ');
          return (
            <div key={t.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={STUFE_ICON[stufe]} ton={info.ton} titel={[t.ticket_nummer, t.titel || 'Ticket'].filter(Boolean).join(' · ')} sub={sub}
                  chip={{ text: info.label, ton: info.ton }} onClick={n && !laeuft ? () => { void weiter(t); } : undefined}
                  ariaLabel={n ? `${t.ticket_nummer ?? 'Ticket'} auf ${stufeLabel(n)} setzen` : undefined} />
              </div>
              {t.mieter_id && <ImmoButton groesse={32} icon={ChatText} aria-label={`Mieter ${mieter} öffnen`} title="Mieter öffnen" onClick={() => openKontakt(t.mieter_id as string, 'mieter')} />}
              <ImmoButton groesse={32} icon={ArrowSquareOut} aria-label={`${t.ticket_nummer ?? 'Ticket'} öffnen`} title="Im Ticket-Bereich öffnen" onClick={() => navigate(`/admin/tickets/${encodeURIComponent(t.id)}`)} />
            </div>
          );
        })}
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Auftrag erteilen</KartenTitel>
        {zugeordnet.length > 0 && zugeordnet.length <= 6 ? (
          <SegmentedControl<string> ariaLabel="Objekt" hoehe={34} className="self-start" wert={objektId ?? ''} onChange={v => setObjektId(v || null)}
            segmente={[...zugeordnet.map(id => ({ key: id, label: objName.get(id) ?? 'Objekt' })), { key: '', label: 'ohne Objekt' }]} />
        ) : (
          <SearchSelect label="Objekt" optionen={objektOptionen} ausgewaehlt={objektId ? objektOptionen.find(o => o.id === objektId) ?? null : null}
            onWahl={o => setObjektId(o.id)} onEntfernen={() => setObjektId(null)} platzhalter="Objekt suchen …" />
        )}
        <FeldRaster minBreite={200}>
          <TextFeld label="Was ist zu tun?" icon={Wrench} voll placeholder="z. B. Heizkörper Wohnung 02 tauschen" wert={text} onChange={setText} />
          <TextFeld label="Wunschtermin" icon={CalendarBlank} type="date" wert={wunsch} onChange={setWunsch} />
          <TextFeld label="Kostenrahmen" icon={Coins} inputMode="decimal" suffix="€" placeholder={String(grenze)} wert={kosten} onChange={setKosten} fehler={kostenFehler} />
        </FeldRaster>
        {ueberGrenze && (
          <>
            <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--warn)' }}>
              Kostenrahmen über der Freigabegrenze von {eur(grenze, { nachkomma: 0 })} – Freigabe durch Eigentümer bzw. WEG-Beschluss nötig.
            </span>
            {objektId && <SchalterZeile an={freigabe} onChange={setFreigabe} klein label="Freigabe beim Eigentümer anfragen" zusatz="Wird unter „Freigaben“ des Eigentümers erfasst (Mietverwaltung/SEV)" />}
          </>
        )}
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
          Legt ein Ticket an und öffnet im Ticket-Bereich die {z.angebot ? 'Angebotsanfrage' : 'Auftragsmail'} an {s.name} zum Versand.
        </span>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton art="akzent" icon={z.angebot ? FileText : PaperPlaneTilt} disabled={laeuft || !text.trim() || !!kostenFehler} onClick={() => { void sende(); }}>
            {laeuft ? 'Wird angelegt …' : z.angebot ? 'Angebot anfordern' : 'Auftrag senden'}
          </ImmoButton>
        </div>
      </InnenKarte>
    </>
  );
}

// ── Rechnungen (Screens 16, 39) ────────────────────────────────────────────────
const LEER_RECHNUNG = (heute: string): RechnungEntwurf => ({ nummer: '', betrag: '', datum: heute, leistung: '', umlagefaehig: false, reverseCharge: false });

export function RechnungenPanel() {
  const s = useDlSeite();
  const navigate = useNavigate();
  const d = s.daten.dienstleister;
  const z = s.daten.zeile;
  const jahr = s.heute.slice(0, 4);
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const [e, setE] = useState<RechnungEntwurf>(() => ({ ...LEER_RECHNUNG(s.heute), reverseCharge: d.reverse_charge_13b === true }));
  const [beleg, setBeleg] = useState<File | null>(null);
  const datei = useRef<HTMLInputElement>(null);
  const k = rechnungKennzahlen(s.daten.rechnungen, jahr);
  const objName = useMemo(() => new Map(s.daten.objekte.map(o => [o.id, objektName(o)])), [s.daten.objekte]);
  const betrag = zahl(e.betrag);
  const fehler = e.nummer || e.betrag ? rechnungFehler(e, betrag) : null;
  const ohneUst = e.reverseCharge || d.kleinunternehmer === true;
  const aufteilung = betrag !== null && !Number.isNaN(betrag) && betrag > 0 ? steuerAufteilung(betrag, ohneUst) : null;
  const setze = <K extends keyof RechnungEntwurf>(key: K) => (v: RechnungEntwurf[K]) => setE(alt => ({ ...alt, [key]: v }));

  const weiter = async (r: (typeof s.daten.rechnungen)[number]) => {
    const st = rechnungStufe(r);
    setLaeuft(true);
    try {
      if (st === 'offen') { await pruefeRechnung(r.id); zeige(`${r.rechnungsnummer ?? 'Rechnung'} geprüft`); }
      else if (st === 'geprueft') { await bezahleRechnung(r); zeige(`${r.rechnungsnummer ?? 'Rechnung'} zur Zahlung freigegeben`); }
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(fehlerText(err), true); } finally { setLaeuft(false); }
  };
  const speichere = async () => {
    if (rechnungFehler(e, betrag) || betrag === null) return;
    setLaeuft(true);
    try {
      const warn = await erfasseRechnung(
        rechnungPayload(e, betrag, { id: d.id, name: s.name, iban: typeof d.iban === 'string' ? d.iban : null, kontoinhaber: typeof d.kontoinhaber === 'string' ? d.kontoinhaber : null, kleinunternehmer: d.kleinunternehmer === true }),
        beleg ? { datei: beleg, dlName: s.name } : null,
      );
      zeige([`Rechnung ${e.nummer.trim()} über ${eur(betrag)} erfasst`, ...warn].join(' · '), warn.length > 0);
      setE({ ...LEER_RECHNUNG(s.heute), reverseCharge: d.reverse_charge_13b === true });
      setBeleg(null);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Offen" wert={eur(k.offen)} wertFarbig icon={SealWarning} ton={k.offen > 0.005 ? 'warn' : 'ok'} />
        <KpiTile layout="kachel" label={`Bezahlt ${jahr}`} wert={eur(k.bezahltJahr, { nachkomma: 0 })} icon={CheckCircle} ton="ok" />
        <KpiTile layout="kachel" label={`Volumen ${jahr}`} wert={eur(k.volumen, { nachkomma: 0 })} icon={ChartBar} ton="acc" />
        <KpiTile layout="kachel" label="§ 35a-Lohnanteil" wert={k.lohnanteil === null ? '–' : eur(k.lohnanteil, { nachkomma: 0 })} sub={k.lohnanteil === null ? 'nicht erfasst' : undefined} icon={Receipt} ton="info" />
      </KpiReihe>
      {bauabzugNoetig(z.handwerk, s.nachweise) && (
        <InnenKarte style={{ background: 'var(--bad-soft)' }} gap={4}>
          <KartenTitel>Bauabzugsteuer</KartenTitel>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
            Die Freistellungsbescheinigung nach § 48b EStG ist abgelaufen oder fehlt. Bei Bauleistungen sind 15 % des Rechnungsbetrags einzubehalten und an das Finanzamt abzuführen, bis eine gültige Bescheinigung vorliegt.
          </span>
        </InnenKarte>
      )}
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts={<ImmoButton groesse={36} icon={ArrowSquareOut} onClick={() => navigate('/admin/rechnungen')}>Zum Rechnungsbereich</ImmoButton>}>
            {`${s.daten.rechnungen.length} ${s.daten.rechnungen.length === 1 ? 'Rechnung' : 'Rechnungen'}`}
          </KartenTitel>
        </div>
        {s.daten.rechnungen.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Noch keine Rechnungen</span>}
        {s.daten.rechnungen.map(r => {
          const st = rechnungStufe(r);
          const info = RECHNUNG_STUFE[st];
          const sub = [r.objekt_id ? objName.get(r.objekt_id) ?? '' : '', r.datum ? deDatum(r.datum) : '', info.aktion ? `antippen: ${info.aktion}` : ''].filter(Boolean).join(' · ');
          return (
            <div key={r.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={Receipt} ton={info.ton} titel={[r.rechnungsnummer, r.bezeichnung].filter(Boolean).join(' · ') || 'Rechnung'} sub={sub}
                  rechts={r.brutto === null ? '–' : eur(r.brutto)} chip={{ text: info.label, ton: info.ton }}
                  onClick={info.aktion && !laeuft ? () => { void weiter(r); } : undefined} ariaLabel={info.aktion ? `${r.rechnungsnummer ?? 'Rechnung'}: ${info.aktion}` : undefined} />
              </div>
              <ImmoButton groesse={32} icon={ArrowSquareOut} aria-label={`${r.rechnungsnummer ?? 'Rechnung'} im Rechnungsbereich öffnen`} title="Im Rechnungsbereich öffnen"
                onClick={() => navigate(`/admin/rechnungen/${encodeURIComponent(r.id)}`)} />
            </div>
          );
        })}
        <span style={{ padding: '6px 8px 4px', fontSize: 11, color: 'var(--ink3)' }}>Ab 3.000 € gilt das Vier-Augen-Prinzip des Rechnungsbereichs.</span>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Rechnung erfassen</KartenTitel>
        <FeldRaster minBreite={180}>
          <TextFeld label="Rechnungsnummer" icon={Receipt} placeholder="RE-…" wert={e.nummer} onChange={setze('nummer')} />
          <TextFeld label="Betrag (brutto)" icon={Coins} inputMode="decimal" suffix="€" placeholder="0,00" wert={e.betrag} onChange={setze('betrag')} />
          <TextFeld label="Rechnungsdatum" icon={CalendarBlank} type="date" wert={e.datum} onChange={setze('datum')} />
          <TextFeld label="Leistung / Ticket" icon={Wrench} voll wert={e.leistung} onChange={setze('leistung')} />
        </FeldRaster>
        <div className="flex flex-col" style={{ gap: 6 }}>
          <SchalterZeile an={e.umlagefaehig} onChange={setze('umlagefaehig')} label="Umlagefähig (Betriebskosten)" />
          <SchalterZeile an={e.reverseCharge} onChange={setze('reverseCharge')} label="§ 13b Reverse Charge" />
        </div>
        {aufteilung && (
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            Netto {eur(aufteilung.netto)} · USt {aufteilung.steuersatz} % {eur(aufteilung.steuer)}{ohneUst ? (e.reverseCharge ? ' (Steuer schuldet der Leistungsempfänger)' : ' (Kleinunternehmer)') : ''}
          </span>
        )}
        {beleg && <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Beleg: {beleg.name} – wird beim Speichern angehängt.</span>}
        {fehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton art="akzent" icon={Check} disabled={laeuft || !!rechnungFehler(e, betrag)} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Rechnung speichern'}</ImmoButton>
          <ImmoButton icon={UploadSimple} disabled={laeuft} onClick={() => datei.current?.click()}>{beleg ? 'Anderen Beleg wählen' : 'Beleg hochladen'}</ImmoButton>
          <input ref={datei} type="file" hidden accept=".pdf,image/*" onChange={ev => { setBeleg(ev.target.files?.[0] ?? null); ev.target.value = ''; }} />
        </div>
      </InnenKarte>
    </>
  );
}

// ── Ticket-Regeln (Screen 21) ──────────────────────────────────────────────────
export function RegelnPanel() {
  const s = useDlSeite();
  const navigate = useNavigate();
  const z = s.daten.zeile;
  const katName = new Map(s.daten.kategorien.map(k => [k.id, k.name]));
  const rolle = z.bevorzugt ? (s.vertretung ? 'als Vertretung' : 'als erste Wahl') : 'nachrangig (nicht bevorzugt)';
  return (
    <>
      <InnenKarte style={GLAS} gap={4}>
        <KartenTitel>{`So wird ${s.name} vorgeschlagen`}</KartenTitel>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
          {z.kategorien.length
            ? `Trifft ein Ticket auf eine Regel der Gewerke ${z.kategorien.join(', ')}, wird dieser Dienstleister ${rolle} vorgeschlagen${z.notfall ? ', bei Notfall-Regeln auch außerhalb der Geschäftszeiten.' : '. Für Notfall-Regeln ist er nicht als Notfallkontakt hinterlegt.'}`
            : 'Ohne Gewerk wird dieser Dienstleister bei Tickets nicht vorgeschlagen.'}
          {!z.aktiv && ' Inaktive Dienstleister werden nie vorgeschlagen.'}
        </span>
      </InnenKarte>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts={<ImmoButton groesse={36} icon={Funnel} onClick={() => navigate('/admin/dienstleister?kategorien=1')}>Regeln verwalten</ImmoButton>}>
            {`${s.regeln.length} ${s.regeln.length === 1 ? 'Regel' : 'Regeln'}`}
          </KartenTitel>
        </div>
        {s.regeln.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Regel für diese Gewerke – unter „Regeln verwalten“ einer Regel ein Gewerk geben.</span>}
        {s.regeln.map(r => (
          <PanelZeile key={r.id} icon={Funnel} ton={r.aktiv ? 'acc' : 'neutral'} aktiv={r.aktiv} titel={`${r.name || 'Regel'}${r.aktiv ? '' : ' (inaktiv)'}`}
            sub={[`${r.stichwoerter.map(x => `„${x}“`).join(', ')} → ${r.gewerk_id ? katName.get(r.gewerk_id) ?? '' : ''}`, r.ist_notfall ? 'Notfall' : '', r.nur_mit_anhang ? 'nur mit Anhang' : '', r.ist_rechtlich_sensibel ? 'rechtlich sensibel' : ''].filter(Boolean).join(' · ')}
            chip={{ text: r.prioritaet, ton: prioTon(r.prioritaet) }} />
        ))}
      </InnenKarte>
    </>
  );
}

// ── Leistung & Bewertung (Screens 13, 22) ──────────────────────────────────────
const STERNE_SEG = [1, 2, 3, 4, 5].map(n => ({ key: String(n), label: '★'.repeat(n) }));

function SternZeile({ label, wert }: { label: string; wert: number | null }) {
  return (
    <div className="flex items-center justify-between" style={{ padding: '10px 0', borderBottom: '1px solid var(--line)', fontSize: 14, gap: 12 }}>
      <span style={{ color: 'var(--ink2)' }}>{label}</span>
      <b className="immo-zahl" style={{ color: wert === null ? 'var(--ink3)' : GOLD }}>{wert === null ? 'noch nicht bewertet' : `${sterne(wert)} ${komma1(wert)}`}</b>
    </div>
  );
}

export function BewertungPanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const z = s.daten.zeile;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const [wertung, setWertung] = useState('4');
  const [kommentar, setKommentar] = useState('');
  const [kriterien, setKriterien] = useState<Record<string, number>>({});
  const tickets = s.daten.tickets;
  const erledigt = tickets.filter(t => { const st = ticketStufe(t.status); return st === 'erledigt' || st === 'abgerechnet'; });
  const bewertet = new Set(s.daten.bewertungen.map(b => b.ticket_id).filter(Boolean));
  const letzter = erledigt.find(t => !bewertet.has(t.id)) ?? null;
  const reaktion = reaktionsStunden(tickets, s.daten.historie);
  const monate = auftraegeJeMonat(tickets, s.heute);
  const max = Math.max(1, ...monate.map(m => m.anzahl));
  const schnitt = kriterienSchnitt(s.daten.bewertungen);
  const erledigt12 = monate.reduce((a, m) => a + m.anzahl, 0);

  const speichere = async () => {
    setLaeuft(true);
    try {
      await speichereBewertung(d.id, { sterne: Number(wertung), kommentar, ticketId: letzter?.id ?? null, kriterien });
      zeige(`Bewertung ${wertung} ★ gespeichert`);
      setKommentar(''); setKriterien({});
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <div className="immo-glas flex items-center min-w-0" style={{ borderRadius: 20, padding: '14px 16px', gap: 12 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: GOLD_SOFT }}><Star weight="fill" size={20} color={GOLD} /></span>
          <span className="flex flex-col min-w-0">
            <span className="immo-zahl" style={{ fontSize: 20, fontWeight: 800, color: GOLD }}>{z.bewertung === null ? '–' : `${komma1(z.bewertung)} ★`}</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Gesamtbewertung</span>
          </span>
        </div>
        <KpiTile layout="kachel" label="Ø Reaktionszeit" wert={stunden(reaktion)} icon={Timer} ton="info" />
        <KpiTile layout="kachel" label="Termintreue" wert={schnitt.termintreue === null ? '–' : `${komma1(schnitt.termintreue)} ★`} sub={schnitt.termintreue === null ? 'noch nicht bewertet' : 'aus Bewertungen'} icon={CalendarBlank} ton="ok" />
        <KpiTile layout="kachel" label="Erledigt (12 Mon.)" wert={erledigt12} icon={CheckCircle} ton="acc" />
      </div>
      <InnenKarte style={GLAS} gap={0}>
        <KartenTitel>Bewertung nach Kriterien</KartenTitel>
        {KRITERIEN.map(k => <SternZeile key={k.key} label={k.label} wert={schnitt[k.key] ?? null} />)}
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Aufträge je Monat</KartenTitel>
        <div className="flex items-end" style={{ gap: 6, height: 110 }} role="img" aria-label="Erledigte Aufträge je Monat">
          {monate.map((m, i) => (
            <div key={m.monat} className="flex flex-col items-center flex-1" style={{ gap: 4 }} title={`${m.monat}: ${m.anzahl} ${m.anzahl === 1 ? 'Auftrag' : 'Aufträge'}`}>
              <div style={{ width: '100%', maxWidth: 28, height: `${Math.max(4, (m.anzahl / max) * 84)}px`, borderRadius: 6, background: i === monate.length - 1 ? 'var(--acc)' : m.anzahl ? 'var(--ok)' : 'var(--fill2)' }} />
              <span style={{ fontSize: 10, color: 'var(--ink3)' }}>{new Date(`${m.monat}-01T00:00:00Z`).toLocaleDateString('de-DE', { month: 'short' }).slice(0, 3)}</span>
            </div>
          ))}
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Letzten Auftrag bewerten</KartenTitel>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{letzter ? `${letzter.ticket_nummer ?? ''} · ${letzter.titel ?? 'Auftrag'}` : 'Kein unbewerteter erledigter Auftrag – Bewertung wird ohne Ticket gespeichert.'}</span>
        <SegmentedControl<string> ariaLabel="Sterne" hoehe={34} className="self-start" wert={wertung} onChange={setWertung} segmente={STERNE_SEG} />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 8 }}>
          {KRITERIEN.map(k => (
            <div key={k.key} className="flex items-center justify-between" style={{ gap: 8, fontSize: 13, padding: '8px 12px', borderRadius: 14, background: 'var(--fill)' }}>
              <span style={{ color: 'var(--ink2)' }}>{k.label}</span>
              <span className="flex" role="radiogroup" aria-label={k.label}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" role="radio" aria-checked={kriterien[k.key] === n} aria-label={`${k.label}: ${n} Sterne`}
                    onClick={() => setKriterien(x => ({ ...x, [k.key]: n }))} className="cursor-pointer"
                    style={{ border: 'none', background: 'transparent', padding: 2, color: (kriterien[k.key] ?? 0) >= n ? GOLD : 'var(--fill3)' }}>
                    <Star weight="fill" size={16} />
                  </button>
                ))}
              </span>
            </div>
          ))}
        </div>
        <textarea value={kommentar} onChange={ev => setKommentar(ev.target.value)} rows={2} aria-label="Kommentar" placeholder="z. B. pünktlich, sauber gearbeitet" style={BEREICH_STIL} />
        <ImmoButton art="akzent" icon={Check} className="self-start" disabled={laeuft} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Bewertung speichern'}</ImmoButton>
      </InnenKarte>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Letzte Bewertungen</KartenTitel></div>
        {s.daten.bewertungen.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Noch keine Bewertungen</span>}
        {s.daten.bewertungen.slice(0, 20).map(b => (
          <PanelZeile key={b.id} icon={Star} ton="acc" titel={`${sterne(b.sterne)} · ${b.kommentar || 'ohne Kommentar'}`}
            sub={[b.autor?.full_name || 'Team', deDatum(b.created_at.slice(0, 10))].join(' · ')} />
        ))}
      </InnenKarte>
    </>
  );
}

